import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../../store';
import { Plan } from './models/Plan.interface';
import { sortPlansByData } from '../../shared/helpers/helpers';
import { Billing, GetPlanOutput } from "@smash-sdk/billing/01-2022";
import { Vat as VatSdk, ComputeVatInput, ComputeVatOutput } from "@smash-sdk/vat/10-2019";
import { AsyncState } from '../../shared/models/async.interface';

interface AvailablePlans extends AsyncState {
    plans: Plan[]
}

interface SelectedPlan extends AsyncState {
    plan: GetPlanOutput['plan'] | null;
}

interface Vat extends AsyncState {
    vat: ComputeVatOutput['vat'] | null;
}

export interface PlanUpgradeState {
    availablePlans: AvailablePlans;
    selectedPlan: SelectedPlan,
    vat: Vat,
}

export const fetchPlans = createAsyncThunk(
    'planUpgrade/fetchPlans',
    async (_, thunkApi) => {
        try {
            const billing = new Billing();
            const { plans } = await billing.listGroupPlans({ groupId: 'Smash Api' });
            return { availablePlans: sortPlansByData(plans) };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const choosePlan = createAsyncThunk(
    'planUpgrade/choosePlan',
    async ({ planIdSelected, defaultCurrency }: { planIdSelected: string, defaultCurrency?: string }, thunkApi) => {
        try {
            const { planUpgrade }: any = thunkApi.getState();
            const availablePlans = planUpgrade.availablePlans.plans
                .map((item: Plan) => ({ ...item, selected: false }))
                .map((item: Plan) => ({
                    ...item,
                    selected: planIdSelected ? !!(item.id === planIdSelected) : !!(item.currency === defaultCurrency && item.name === process.env.REACT_APP_SMASH_PLAN_API_GROUP_ID)
                }));

            const plan = availablePlans.find((plan: any) => plan.selected);
            return { plan, availablePlans: sortPlansByData(availablePlans) };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const computeVat = createAsyncThunk(
    'planUpgrade/computeVat',
    async (input: ComputeVatInput, thunkApi) => {
        try {
            return await new VatSdk().computeVat(input);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const initialState: PlanUpgradeState = {
    availablePlans: {
        plans: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    selectedPlan: {
        plan: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    vat: {
        vat: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
}

export const planUpgradeSlice = createSlice({
    name: 'planUpgrade',
    initialState,
    reducers: {
        resetPlanUpgrade: (state) => ({
            availablePlans: {
                plans: [],
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            selectedPlan: {
                plan: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            vat: {
                vat: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
        }),
        resetVat: (state) => {
            state.vat = initialState.vat;
            return state;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlans.pending, (state) => {
                state.availablePlans.isLoading = true;
                state.availablePlans.isSuccess = false;
                state.availablePlans.isError = false;
            })
            .addCase(fetchPlans.fulfilled, (state, action) => {
                state.availablePlans.isSuccess = true;
                state.availablePlans.isLoading = false;
                state.availablePlans.plans = action.payload.availablePlans;
            })
            .addCase(fetchPlans.rejected, (state) => {
                state.availablePlans.isSuccess = false;
                state.availablePlans.isLoading = false;
                state.availablePlans.isError = true;
                state.availablePlans = initialState.availablePlans;
            })
            .addCase(choosePlan.pending, (state) => {
                state.selectedPlan.isLoading = true;
                state.selectedPlan.isSuccess = false;
                state.selectedPlan.isError = false;
            })
            .addCase(choosePlan.fulfilled, (state, action) => {
                state.selectedPlan.isSuccess = true;
                state.selectedPlan.isLoading = false;
                state.selectedPlan.plan = action.payload.plan;
                state.availablePlans.plans = action.payload.availablePlans;
            })
            .addCase(choosePlan.rejected, (state) => {
                state.selectedPlan.isSuccess = false;
                state.selectedPlan.isLoading = false;
                state.selectedPlan.isError = true;
                state.selectedPlan.plan = null;
            })
            .addCase(computeVat.pending, (state) => {
                state.vat.isLoading = true;
                state.vat.isSuccess = false;
                state.vat.isError = false;
            })
            .addCase(computeVat.fulfilled, (state, action) => {
                state.vat.isLoading = false;
                state.vat.isSuccess = true;
                state.vat.vat = action.payload.vat as ComputeVatOutput['vat'];
            })
            .addCase(computeVat.rejected, (state) => {
                state.vat.isSuccess = false;
                state.vat.isLoading = false;
                state.vat.isError = true;
            })
    }
});


export const { resetPlanUpgrade, resetVat } = planUpgradeSlice.actions;
export const planUpgradeState = (state: RootState) => state.planUpgrade;

export default planUpgradeSlice.reducer;